import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  formatDate, 
  formatNumber, 
  calculateLaycanStatus, 
  getLaycanStatusColor,
  getPortStayDeviationColor,
  intervalToDays
} from '../../lib/pcerUtils';

export default function PCERReportTable({ reportData, loading }) {
  return (
    <Card className="flex-1">
      <CardHeader>
        <CardTitle>Report Data</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-600">Loading report data...</div>
          </div>
        ) : reportData.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-gray-600">
              <p className="mb-2">No data available</p>
              <p className="text-sm">Select date range and click "Load Report" to view data</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto h-[calc(100vh-350px)] border rounded-lg">
            <table className="min-w-max w-full text-sm border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[140px]">Port Operation #</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[150px]">Vessel</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[120px]">Voyage Number</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[150px]">Port/Position</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[110px]">ATA</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[110px]">ATS</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700 border-b whitespace-nowrap min-w-[140px]">Planned Port Days</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700 border-b whitespace-nowrap min-w-[140px]">Actual Port Days</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[200px]">Comments</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[110px]">Laydays</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[120px]">Cancelation</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700 border-b whitespace-nowrap min-w-[160px]">Lay can Deviation (Days)</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-700 border-b whitespace-nowrap min-w-[140px]">Lay can Status</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700 border-b whitespace-nowrap min-w-[180px]">Port Stay Deviation (Days)</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => {
                  const actualPortDays = row.actual_port_days !== null ? parseFloat(row.actual_port_days) : null;
                  const plannedDays = row.planned_days !== null ? parseFloat(row.planned_days) : 
                                      (row.working_days && row.idle_days ? 
                                        intervalToDays(row.working_days) + intervalToDays(row.idle_days) : null);
                  const portStayDeviation = (actualPortDays !== null && plannedDays !== null) ? 
                                            actualPortDays - plannedDays : null;
                  const laycanStatus = calculateLaycanStatus(row.ata, row.laycan_start_date, row.laycan_end_date);

                  return (
                    <tr key={index} className="hover:bg-gray-50 border-b">
                      <td className="px-4 py-3 whitespace-nowrap">{row.port_operation_number || row.port_operation_id || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{row.vessel_name || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{row.voyage_number || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{row.port_name || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(row.ata)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(row.ats)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">{formatNumber(plannedDays)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">{formatNumber(actualPortDays)}</td>
                      <td className="px-4 py-3 max-w-xs" title={row.remarks}>
                        {row.remarks || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(row.laycan_start_date)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(row.laycan_end_date)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">{formatNumber(row.laycan_deviation_days)}</td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getLaycanStatusColor(laycanStatus)}`}>
                          {laycanStatus}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-right whitespace-nowrap ${getPortStayDeviationColor(portStayDeviation)}`}>
                        {formatNumber(portStayDeviation)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
